import { useTranslation } from "react-i18next";
import { Info, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useImagePreview } from "@/context/ImagePreview";
import { cn } from "@/utils/cn";
import type { IChatConversation, IChatUserSummary } from "@/types/(my-space)/chat.interface";
import { ChatAvatar } from "./ChatAvatar";
import { conversationTitle } from "../_lib/chat-labels";

/** What the popup draws: a chat's picture and title. */
export type ProfileSubject = Pick<
  IChatConversation,
  "id" | "type" | "name" | "imageUrl" | "metadata" | "isSelf"
> & { peer: Pick<IChatUserSummary, "firstName" | "lastName" | "profileImage"> | null };

/**
 * Clicking a chat's picture in the list, like WhatsApp: the picture large with
 * the name across it, and Message · Info underneath (the portal places no
 * calls). The picture itself opens in the image viewer.
 */
export const ProfilePopup = ({
  conversation: c,
  onClose,
  onMessage,
  onInfo,
  onOpenImage,
}: {
  /** A chat, or a person shaped as their direct chat (see `personProfile`). */
  conversation: ProfileSubject | null;
  onClose: () => void;
  /** Omit both (as Chat info does — it is that chat's page): picture and name only. */
  onMessage?: (id: number) => void;
  onInfo?: (id: number) => void;
  /** Open the picture full size; defaults to the app-wide viewer. */
  onOpenImage?: (url: string) => void;
}) => {
  const { t } = useTranslation("chat");
  const preview = useImagePreview();
  const openPreview = (urls: string[]) =>
    onOpenImage ? onOpenImage(urls[0]) : preview(urls);
  const actions = [
    onMessage && { key: "message", icon: MessageCircle, label: t("info.message"), run: onMessage },
    onInfo && { key: "info", icon: Info, label: t("info.title"), run: onInfo },
  ].filter((a): a is { key: string; icon: typeof Info; label: string; run: (id: number) => void } => !!a);
  const title = c ? conversationTitle(t, c) : "";
  const image = c ? (c.type === "direct" ? c.peer?.profileImage : c.imageUrl) : null;

  return (
    <Dialog open={!!c} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="w-72 gap-0 overflow-hidden rounded-2xl border-0 p-0 sm:max-w-72"
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {c ? (
          <>
            <div className="relative aspect-square w-full bg-primary-muted">
              {image ? (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    openPreview([image]);
                  }}
                  aria-label={t("profile.view_photo")}
                  className="block size-full cursor-zoom-in"
                >
                  <img src={image} alt="" className="size-full object-cover" />
                </button>
              ) : (
                <div className="flex size-full items-center justify-center">
                  <ChatAvatar name={title} kind={c.type} size="xl" className="scale-150" />
                </div>
              )}
              {/* Name across the top, readable on any picture. */}
              <div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-black/55 to-transparent px-4 pt-3 pb-6">
                <p className="truncate text-base font-semibold text-white">{title}</p>
              </div>
            </div>
            {actions.length ? (
            <div className="flex divide-x divide-border border-t border-border rtl:divide-x-reverse">
              {actions.map(({ key, icon: Icon, label, run }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    onClose();
                    run(c.id);
                  }}
                  aria-label={label}
                  title={label}
                  className={cn(
                    "flex h-12 flex-1 items-center justify-center text-primary transition-colors hover:bg-muted",
                  )}
                >
                  <Icon className="size-5" />
                </button>
              ))}
            </div>
            ) : null}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
